import { useEffect } from "react";

/**
 * A part of a page opened by name, as the header's search opens Settings at Layouts on this device,
 * glows softly around its border three times as it comes into view, so the eye finds it among the
 * others (Tyler, 2026-10-03). The class carries the glow (`styles.css`); this puts it on the part the
 * address names and takes it off once the glow is done.
 */
export const ARRIVED = "arrived";

/** How long to wait for the part to be drawn: some pages draw theirs once their data has come. */
const WAIT_MS = 3000;

/**
 * Glows the element whose id is `hash`. `visit` tells one opening from the next, so opening the same
 * part again, from search while already on it, glows it again.
 */
export function useArrivalGlow(hash: string, visit: string | undefined): void {
  // biome-ignore lint/correctness/useExhaustiveDependencies: each visit is a new arrival
  useEffect(() => {
    if (!hash) return;
    let el: HTMLElement | null = null;
    let timer: number | undefined;
    const started = Date.now();
    const done = () => el?.classList.remove(ARRIVED);
    const find = () => {
      el = document.getElementById(hash);
      if (el) {
        el.classList.remove(ARRIVED);
        // Reading its size lays the page out, so the glow starts over if it was already glowing.
        el.getBoundingClientRect();
        el.classList.add(ARRIVED);
        el.addEventListener("animationend", done, { once: true });
      } else if (Date.now() - started < WAIT_MS) {
        timer = window.setTimeout(find, 100);
      }
    };
    find();
    return () => {
      window.clearTimeout(timer);
      el?.removeEventListener("animationend", done);
      done();
    };
  }, [hash, visit]);
}
